import { CommonModule } from '@angular/common';
import { LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ADDITIONAL_FLOWS, Flow } from '../core/services/command/command.service';
import { CoreModule } from '../core/core.module';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { OrderListModule } from 'primeng/orderlist';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { TagModule } from 'primeng/tag';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from '../app-routing.module';
import { SharedModule } from '../shared-module/shared.module';
import { TourSettingsDialogComponent } from './components/tour-settings-dialog/tour-settings-dialog.component';
import { ToursDetailPlanningMapComponent } from './components/tours-detail-planning-map/tours-detail-planning-map.component';
import { ToursPlannerTasksListComponent } from './components/tours-planner-tasks-list/tours-planner-tasks-list.component';
import { ToursPlannerToursListComponent } from './components/tours-planner-tours-list/tours-planner-tours-list.component';
import { ToursDetailPageComponent } from './containers/tours-detail-page/tours-detail-page.component';
import { ToursPlannerPageComponent } from './containers/tours-planner-page/tours-planner-page.component';
import { AddressService } from './shared/services/address/address.service';
import { TaskService } from './shared/services/task/task.service';
import { TourService } from './shared/services/tour/tour.service';

// Import locale data for 'de'
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { CalendarModule } from 'primeng/calendar';
import { PaginatorModule } from 'primeng/paginator';
import { PanelModule } from 'primeng/panel';
import { SplitterModule } from 'primeng/splitter';
import { TableModule } from 'primeng/table';
import { RentModule } from '../rent-module/rent.module';
import { TaskCreateDialogComponent } from './components/task-create-dialog/task-create-dialog.component';
import { TaskDialogComponent } from './components/task-dialog/task-dialog.component';
import { ToursPlannerToursMultiComponent } from './components/tours-planner-tours-multi/tours-planner-tours-multi.component';
import { ToursPlannerToursComponent } from './components/tours-planner-tours/tours-planner-tours.component';
import { ComposeAtomicTaskCommandCustomFlow } from './shared/customFlows/composeAtomicTaskCommandCustomFlow';
import { CreateDefaultTaskCommandCustomFlow } from './shared/customFlows/createDefaultTaskCommandCustomFlow';
import { OperatedRentalTaskUpdateAddressCommandCustomFlow } from './shared/customFlows/OperatedRentalTaskUpdateAddressCommandCustomFlow ';
import { RentalResourceAssignmentTaskUpdateAddressCommandCustomFlow } from './shared/customFlows/RentalResourceAssignmentTaskUpdateAddressCommandCustomFlow';
import { SplitCompositTaskCommandCustomFlow } from './shared/customFlows/splitCompositTaskCommandCustomFlow';
import { TourCreateCommandCustomFlow } from './shared/customFlows/tourCreateCommandCustomFlow';
import { TourFinishCommandCustomFlow } from './shared/customFlows/tourFinishCommandCustomFlow';
import { TourUpdateCommandCustomFlow } from './shared/customFlows/tourUpdateCommandCustomFlow';
import { UpdateDefaultTaskCommandCustomFlow } from './shared/customFlows/updateDefaultTaskCommandCustomFlow';
import { DragDropMonitorService } from './shared/dragDrop/drag-drop-monitor.service';
import { DragPreviewService } from './shared/dragDrop/drag-preview.service';
import { NxDragDropPanelDirective } from './shared/dragDrop/nx-drag-drop-panel.directive';
import { NxDragItemDirective } from './shared/dragDrop/nx-drag-item.directive';
import { NxDropListDirective } from './shared/dragDrop/nx-drop-list.directive';
import { TourPlannerService } from './shared/services/tour/tour-planner.service';
import { TaskCreateCommandCustomFlow } from './shared/customFlows/taskCreateCommandCustomFlow';
import { TaskUpdateCommandCustomFlow } from './shared/customFlows/taskUpdateCommandCustomFlow';

// Register the locale data
registerLocaleData(localeDe, 'de');

@NgModule({
  declarations: [
    ToursPlannerPageComponent,
    ToursPlannerTasksListComponent,
    ToursPlannerToursListComponent,
    TourSettingsDialogComponent,
    ToursDetailPageComponent,
    ToursDetailPlanningMapComponent,
    ToursPlannerToursComponent,
    ToursPlannerToursMultiComponent,
    TaskDialogComponent,
    TaskCreateDialogComponent,
    NxDropListDirective,
    NxDragItemDirective,
    NxDragDropPanelDirective
  ],
  imports: [
    CommonModule,
    AppRoutingModule,
    CoreModule.forRoot(environment, [
          {
            provide: ADDITIONAL_FLOWS,
            multi: true,
            useFactory: (
              createDefaultTaskCommandCustomFlow: CreateDefaultTaskCommandCustomFlow,
              updateDefaultTaskCommandCustomFlow: UpdateDefaultTaskCommandCustomFlow,
              rentalResourceAssignmentTaskUpdateAddressCommandCustomFlow: RentalResourceAssignmentTaskUpdateAddressCommandCustomFlow,
              splitCompositTaskCommandCustomFlow: SplitCompositTaskCommandCustomFlow,
              composeAtomicTaskCommandCustomFlow: ComposeAtomicTaskCommandCustomFlow,
              operatedRentalTaskUpdateAddressCommandCustomFlow: OperatedRentalTaskUpdateAddressCommandCustomFlow,
              tourCreateCommandCustomFlow: TourCreateCommandCustomFlow,
              tourFinishCommandCustomFlow: TourFinishCommandCustomFlow,
              tourUpdateCommandCustomFlow: TourUpdateCommandCustomFlow,
              taskCreateCommandCustomFlow: TaskCreateCommandCustomFlow,
              taskUpdateCommandCustomFlow: TaskUpdateCommandCustomFlow
            ) => {
              const additionalFlows = new Map<string, Flow>();
              additionalFlows.set('createDefaultTaskCommandCustomFlow', createDefaultTaskCommandCustomFlow);
              additionalFlows.set('updateDefaultTaskCommandCustomFlow', updateDefaultTaskCommandCustomFlow);
              additionalFlows.set('rentalResourceAssignmentTaskUpdateAddressCommandCustomFlow', rentalResourceAssignmentTaskUpdateAddressCommandCustomFlow);
              additionalFlows.set('splitCompositTaskCommandCustomFlow', splitCompositTaskCommandCustomFlow);
              additionalFlows.set('composeAtomicTaskCommandCustomFlow', composeAtomicTaskCommandCustomFlow);
              additionalFlows.set('operatedRentalTaskUpdateAddressCommandCustomFlow', operatedRentalTaskUpdateAddressCommandCustomFlow);
              additionalFlows.set('tourCreateCommandCustomFlow', tourCreateCommandCustomFlow);
              additionalFlows.set('tourFinishCommandCustomFlow', tourFinishCommandCustomFlow);
              additionalFlows.set('tourUpdateCommandCustomFlow', tourUpdateCommandCustomFlow);
              additionalFlows.set('taskCreateCommandCustomFlow', taskCreateCommandCustomFlow);
              additionalFlows.set('taskUpdateCommandCustomFlow', taskUpdateCommandCustomFlow);
              return additionalFlows;
            },
            deps: [
              CreateDefaultTaskCommandCustomFlow,
              UpdateDefaultTaskCommandCustomFlow,
              RentalResourceAssignmentTaskUpdateAddressCommandCustomFlow,
              SplitCompositTaskCommandCustomFlow,
              ComposeAtomicTaskCommandCustomFlow,
              OperatedRentalTaskUpdateAddressCommandCustomFlow,
              TourCreateCommandCustomFlow,
              TourFinishCommandCustomFlow,
              TourUpdateCommandCustomFlow,
              TaskCreateCommandCustomFlow,
              TaskUpdateCommandCustomFlow
            ]
          },
        ]),
    SharedModule,
    ReactiveFormsModule,
    DropdownModule,
    TableModule,
    FormsModule,
    OverlayPanelModule,
    OrderListModule,
    CheckboxModule,
    TagModule,
    CalendarModule,
    PanelModule,
    SplitterModule,
    RentModule,
    PaginatorModule,
    ButtonModule,
    TooltipModule,
  ],
  providers: [
    TourService,
    TaskService,
    AddressService,
    { provide: LOCALE_ID, useValue: 'de' },
    TourPlannerService,
    DragDropMonitorService,
    DragPreviewService
  ],
  exports: [
    TaskCreateDialogComponent
  ]
})
export class TourPlanningModule { }
